import { ApiOutlined, CodeOutlined, RocketOutlined } from "@ant-design/icons";
import { Card, Space, Tag, Typography } from "antd";

const { Title, Paragraph, Link, Text } = Typography;

export default function About() {
  // Dynamische Swagger URL basierend auf Environment
  // Funktioniert mit local (/api), dev (Docker), und prod (externe URL)
  const getSwaggerUrl = () => {
    const apiUrl = import.meta.env.VITE_API_URL || "/api";

    // Wenn API URL relativ ist (/api)
    if (apiUrl.startsWith("/")) {
      // Local dev: Frontend auf 5173, Backend auf 8000
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
        return `http://${window.location.hostname}:8000/docs`;
      }
      // Production: Frontend und API auf gleiche Domain
      return `${window.location.origin}/docs`;
    }

    // Wenn absolut URL (Dev Docker/Prod): entferne /api Suffix und hänge /docs an
    // Dev: http://backend:8000/api → http://backend:8000/docs
    // Prod: https://api.example.com/api → https://api.example.com/docs
    const baseUrl = apiUrl.replace(/\/api\/?$/, "");
    return `${baseUrl}/docs`;
  };

  return (
    <Space direction="vertical" size="large" style={{ width: "100%", maxWidth: 800 }}>
      <Card>
        <Title level={2}>
          <RocketOutlined /> Über dieses Template
        </Title>
        <Paragraph>
          Dieses Template nutzt <strong>React + Vite + FastAPI + Pydantic</strong> als modernen
          Starter für hochperformante Fullstack-Projekte mit asynchronen APIs.
          <br />
          Als UI Component Kit wird <strong>Ant Design 5</strong> verwendet.
          <br />
          Moderne Development Tools: <strong>Biome</strong> (Rust) für Frontend und{" "}
          <strong>Ruff</strong> (Rust) für Backend, beide integriert mit Husky Git Hooks für
          automatische Code-Qualität.
          <br />
          <Text type="warning">🏛️ <strong>Legacy Version:</strong> Dieses Template verwendet <strong>JSX</strong> (nicht TypeScript). Für TypeScript-Projekte nutze das neue Template!!!</Text>
        </Paragraph>
      </Card>

      <Card
        title={
          <>
            <CodeOutlined /> Frontend Stack
          </>
        }
      >
        <ul>
          <li>
            <strong>Vite 5</strong> - Lightning-fast Build-Tool
          </li>
          <li>
            <strong>React 18</strong> - UI Library mit Hooks
          </li>
          <li>
            <strong>React Router 6</strong> - Client-side Routing
          </li>
          <li>
            <strong>Ant Design 5</strong> - Enterprise UI Components
          </li>
          <li>
            <strong>Biome</strong> - Linting & Formatting (Rust)
          </li>
          <li>
            <strong>Vitest</strong> - Unit Testing
          </li>
          <li>
            <strong>npm</strong> - Package Manager
          </li>
          <li>
            <Text type="secondary">🏛️ <strong>JSX</strong> (nicht TypeScript) - Legacy Variante!!!</Text>
          </li>
        </ul>
      </Card>

      <Card
        title={
          <>
            <ApiOutlined /> Backend Stack
          </>
        }
      >
        <ul>
          <li>
            <strong>FastAPI 0.128</strong> - Modern Async Python Web Framework
          </li>
          <li>
            <strong>Pydantic v2</strong> - Type-safe Request/Response Validation
          </li>
          <li>
            <strong>SQLAlchemy 2.0 Async</strong> - Async ORM & Database
          </li>
          <li>
            <strong>PyJWT</strong> - JWT Authentication (Stateless)
          </li>
          <li>
            <strong>PyOTP</strong> - TOTP/2FA Support
          </li>
          <li>
            <strong>Alembic</strong> - Database Migrations
          </li>
          <li>
            <strong>Uvicorn</strong> - Production ASGI Server
          </li>
          <li>
            <strong>Ruff</strong> - Python Linting & Formatting (Rust)
          </li>
          <li>
            <strong>pytest + pytest-asyncio</strong> - Async Testing Framework
          </li>
          <li>
            <strong>Mamba/Conda</strong> - Package & Environment Manager
          </li>
        </ul>
      </Card>

      <Card title="🐳 Development & Production Setup">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div>
            <Tag color="blue">local</Tag>
            <Text strong> Local Development</Text>
            <Paragraph style={{ marginTop: 8 }}>
              • Uvicorn Dev Server (mit --reload)
              <br />• Vite HMR (Hot Module Replacement)
              <br />• http://localhost:5173 (Frontend)
              <br />• http://localhost:8000 (Backend + Swagger UI)
            </Paragraph>
          </div>

          <div>
            <Tag color="orange">dev</Tag>
            <Text strong> Docker Development</Text>
            <Paragraph style={{ marginTop: 8 }}>
              • Uvicorn in Container
              <br />• Hot Reload für Code-Änderungen
              <br />• Volume Mounts für schnelle Entwicklung
              <br />• Database Persistence
            </Paragraph>
          </div>

          <div>
            <Tag color="green">main</Tag>
            <Text strong> Production</Text>
            <Paragraph style={{ marginTop: 8 }}>
              • Uvicorn mit Gunicorn Worker
              <br />• Multi-Stage Docker Build
              <br />• Optimierte Images
              <br />• Database Persistence
              <br />• Health Checks & Restart Policies
            </Paragraph>
          </div>
        </Space>
      </Card>

      <Card title="📖 API Documentation">
        <Paragraph>Die vollständige API-Dokumentation ist verfügbar über Swagger UI:</Paragraph>
        <Paragraph>
          <Link href={getSwaggerUrl()} target="_blank" strong>
            {getSwaggerUrl()} →
          </Link>
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: "12px" }}>
          Automatisch generiert mit FastAPI + OpenAPI 3.0
        </Paragraph>
      </Card>

      <Card title="🚀 Features">
        <ul>
          <li>✅ Multi-Environment Setup (local, dev, prod)</li>
          <li>✅ Hot Module Replacement (HMR)</li>
          <li>✅ Auto-generated API Docs (Swagger)</li>
          <li>✅ Database Migrations</li>
          <li>✅ Schema Validation</li>
          <li>✅ CORS Configuration</li>
          <li>✅ Error Boundaries</li>
          <li>✅ Loading States</li>
          <li>✅ Code Formatting (Biome, Ruff)</li>
          <li>✅ Git Hooks (Husky)</li>
          <li>✅ Docker Multi-Stage Build</li>
          <li>✅ Traefik Integration</li>
          <li>✅ SSL/TLS Support</li>
        </ul>
      </Card>

      <Card title="📦 Aktuelles Environment">
        <Space direction="vertical">
          <Text>
            <strong>API URL:</strong> {import.meta.env.VITE_API_URL || "/api"}
          </Text>
          <Text>
            <strong>Mode:</strong> {import.meta.env.MODE}
          </Text>
          <Text>
            <strong>Base URL:</strong> {window.location.origin}
          </Text>
        </Space>
      </Card>
    </Space>
  );
}
